import React, { useMemo, useState } from 'react';
import SpinnerComp from '@/components/loaders/SpinnerComp';
import { NoDataAvailableWithText } from '@/components/cards/NoDataAvailable';

/**
 * Column definition for DataTable
 * @template T - The type of data row
 */
export interface Column<T = any> {
    /** Unique identifier for the column */
    key: string;
    /** Display label for the column header */
    label: React.ReactNode;
    /** 
     * Accessor to get the value from the row data.
     * Can be a string (property name) or a function that returns React.ReactNode
     */
    accessor?: string | ((row: T) => React.ReactNode);
    /** Whether the column is sortable */
    sortable?: boolean;
    /** Width of the column (e.g., '20%', '150px') */
    width?: string;
    /** Text alignment for the column */
    align?: 'left' | 'center' | 'right';
    /** Additional CSS class for the column */
    className?: string;
    /** Additional CSS class for the header cell */
    headerClassName?: string;
    /** Additional CSS class for the data cells */
    cellClassName?: string;
    /** Custom render function for the cell content */
    render?: (value: any, row: T, index: number) => React.ReactNode;
}

/**
 * Props for DataTable component
 * @template T - The type of data row
 */
export interface DataTableProps<T = any> {
    /** Type of the data */
    type: string;
    /** Array of data objects to display */
    data: T[];
    /** Array of column definitions */
    columns: Column<T>[];
    /** Function to extract unique key from each row */
    keyExtractor?: (row: T, index: number) => string | number;
    /** Additional CSS class for the wrapper div */
    className?: string;
    /** Additional CSS class for the table element */
    tableClassName?: string;
    /** Callback function when a row is clicked */
    onRowClick?: (row: T, index: number, event: React.MouseEvent<HTMLTableRowElement>) => void;
    /** Optional class name for each data row */
    getRowClassName?: (row: T, index: number) => string | undefined;
    /** Whether the table is in loading state */
    loading?: boolean;
    /** Whether more data is being loaded (for infinite scroll) */
    loadingMore?: boolean;
    /** Whether the header should be sticky */
    stickyHeader?: boolean;
    /** Whether to use fixed table layout */
    fixedLayout?: boolean;
    /** When true, removes the top border from the table header */
    hideHeaderTopBorder?: boolean;
}
const DataTable = <T extends Record<string, any>>({
    type = '',
    data,
    columns,
    keyExtractor,
    className = '',
    tableClassName = '',
    onRowClick,
    getRowClassName,
    loading = false,
    loadingMore = false,
    stickyHeader = false,
    fixedLayout = true,
    hideHeaderTopBorder = false,
}: DataTableProps<T>) => {
    // Normalize to array so empty check and map use the same value (avoids stale/empty when parent has data)
    const rows = Array.isArray(data) ? data : [];
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
    const getRowKey = (row: T, index: number): string | number => {
        if (keyExtractor) {
            return keyExtractor(row, index);
        }
        // Always include index to ensure uniqueness, even if IDs are duplicated
        const id = row.id || row._id;
        return id ? `${id}-${index}` : index;
    };

    const getCellValue = (column: Column<T>, row: T, index: number): React.ReactNode => {
        if (column.render) {
            return column.render(getAccessorValue(column, row), row, index);
        }

        const value = getAccessorValue(column, row);
        return value !== null && value !== undefined ? String(value) : '';
    };

    const getAccessorValue = (column: Column<T>, row: T): any => {
        if (typeof column.accessor === 'function') {
            return column.accessor(row);
        }
        if (typeof column.accessor === 'string') {
            return row[column.accessor];
        }
        // If no accessor, try to use the key
        return row[column.key];
    };

    const getColumnAlign = (column: Column<T>): React.CSSProperties => {
        const align = column.align || 'left';
        return {
            textAlign: align,
        };
    };

    const getComparableValue = (value: any): string | number => {
        if (value == null) return "";
        if (typeof value === "number") return value;
        if (typeof value === "boolean") return value ? 1 : 0;
        if (typeof value === "string") {
            const numericValue = Number(value);
            if (!Number.isNaN(numericValue) && value.trim() !== "") return numericValue;
            const dateValue = Date.parse(value);
            if (!Number.isNaN(dateValue)) return dateValue;
            return value.toLowerCase();
        }
        return String(value).toLowerCase();
    };

    const sortedRows = useMemo(() => {
        if (!sortConfig) return rows;
        const column = columns.find((col) => col.key === sortConfig.key);
        if (!column) return rows;

        const sorted = [...rows].sort((a, b) => {
            const aValue = getComparableValue(getAccessorValue(column, a));
            const bValue = getComparableValue(getAccessorValue(column, b));
            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [rows, columns, sortConfig]);

    const handleSort = (column: Column<T>) => {
        if (!column.sortable) return;
        setSortConfig((prev) => {
            if (!prev || prev.key !== column.key) {
                return { key: column.key, direction: "asc" };
            }
            return { key: column.key, direction: prev.direction === "asc" ? "desc" : "asc" };
        });
    };

    if (loading) {
        return <SpinnerComp />
    }

    if (rows.length === 0) {
        return <NoDataAvailableWithText type={type} />
    }

    return (
        <div className={className} style={{ overflowX: 'auto', overflowY: 'hidden', height: '1000px !important' }}>
            <table
                className={`nk-tb-list nk-tb-ulist table-weight ${tableClassName}`}
                style={{
                    tableLayout: fixedLayout ? 'fixed' : 'auto',
                    width: '100%',
                    height: '500px !important',
                    position: 'relative',
                }}
            >
                <thead style={{
                    ...(stickyHeader ? { position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--white)' } : {}),
                    ...(hideHeaderTopBorder ? { borderTop: 'none' } : {}),
                }}>
                    <tr className="nk-tb-item nk-tb-head">
                        {columns.map((column, index) => (
                            <th
                                key={column.key || index}
                                className={`nk-tb-col ${column.headerClassName || ''}`}
                                style={{
                                    width: column.width,
                                    ...getColumnAlign(column),
                                    ...(hideHeaderTopBorder ? { borderTop: 'none' } : {}),
                                    ...(column.sortable ? { cursor: "pointer", userSelect: "none" } : {}),
                                }}
                                onClick={() => handleSort(column)}
                            >
                                <span className="sub-text">
                                    {column.label}
                                    {column.sortable && sortConfig?.key === column.key && (
                                        <span className="ml-1">{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                                    )}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="tb-odr-body" style={{ position: 'relative' }}>
                    {sortedRows.map((row, rowIndex) => (
                        <tr
                            key={getRowKey(row, rowIndex)}
                            className={`nk-tb-item${getRowClassName?.(row, rowIndex) ? ` ${getRowClassName(row, rowIndex)}` : ""}`}
                            onClick={(event) => {
                                const target = event.target as HTMLElement;
                                if (target?.closest('[data-row-click-ignore="true"]')) {
                                    return;
                                }
                                onRowClick?.(row, rowIndex, event);
                            }}
                            style={onRowClick ? { cursor: 'pointer' } : {}}
                        >
                            {columns.map((column, colIndex) => (
                                <td
                                    key={`${getRowKey(row, rowIndex)}-${column.key || colIndex}`}
                                    className={`nk-tb-col ${column.cellClassName || ''} ${column.className || ''}`}
                                    style={{
                                        ...getColumnAlign(column),
                                        ...(column.cellClassName?.includes('overflow-visible') ? { overflow: 'visible', position: 'relative' } : {})
                                    }}
                                >
                                    {getCellValue(column, row, rowIndex)}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {loadingMore && (
                        <tr className="nk-tb-item">
                            <td colSpan={columns.length} className="text-center" style={{ textAlign: 'center', padding: '20px' }}>
                                <SpinnerComp />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default DataTable;
